'use client';

import '/public/static/css/module/lnb.scss';
import '/public/static/css/soop.scss';
import '/public/static/globals.scss';
import '../../../sentry.client.config.js';
import '@soop-ui/soop-ui/styles/global.css';

import React, {useEffect, useMemo, useState, Suspense, lazy} from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from 'react-router-dom';

import HelmetLoader from '@/app/(main)/HelmetLoader';
import MyPlus from '@/app/(main)/my-plus/index';
import DirectoryRoute from '@/app/(main)/routes/DirectoryRoute';
import LiveRoute from '@/app/(main)/routes/LiveRoute';
import MyRoute from '@/app/(main)/routes/MyRoute';
import PrivateRoute from '@/app/(main)/routes/PrivateRoute';
import SearchRoute from '@/app/(main)/routes/SearchRoute';
import StarRankRoute from './routes/StarRankRoute';

import {getCookie} from '@/utils/main';
import Layout from '@/app/(main)/routes/layout';
import {ROUTE} from '@/constants/main/route';
import useGlobalUIStore from '@/stores';
import {setPosterImageObserver} from '@/utils';
import ResizeObserver from 'resize-observer-polyfill';
import {useGlobalErrorHandler} from '@/components/common/hooks/useGlobalErrorHandler.jsx';
import {lowerStylePriority} from '@soop-ui/soop-ui';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {useAppQueryClient} from '@/hooks/channel/useAppQueryClient';
import {
  useStationActions,
  useStationUIActions,
} from '@/stores/channel/station/hooks';
import {
  NODE_STATION_ERROR_CODE,
  NODE_STATION_ERROR_CODE_VALUE,
  PHP_STATION_ERROR_CODE,
  PHP_STATION_ERROR_CODE_VALUE,
} from '@/api/channel/constants';
import ErrorFallback from '@/components/common/ErrorFallback';
import {normalizePath, createSafeHistoryUrl} from '@/utils/urlNormalizer';
import SurveyLayout from '@/components/help/common/SurveyLayout';
import {HelpMyInquirySurvey} from '@/components/help/pc';

import {installSoopTokens} from '@soop-ui/soop-ui/install';
installSoopTokens({policy: 'first', source: 'soop-kr', lockHost: true});

/**
 * 사파리 15 버전 이하에 특정 클래스를 추가
 *
 * @desc UI팀에서 사파리 이전 버전 대응을 위해 특정 클래스를 추가해 달라는 요청이 옴 (사파리 15 버전이하로 넣어달라고 하심)
 */
const addIos15LessClassForSafari = () => {
  const {userAgent} = navigator;
  const safariMatch = userAgent.match(/Version\/(\d+)\.(\d+).*Safari/);

  if (safariMatch) {
    const majorVersion = parseInt(safariMatch[1], 10);

    // Safari 15.x
    if (majorVersion <= 15) {
      document.body.classList.add('ios15_less');
    }
  }
};

// useLocation은 Router 내부에서 사용할 수 있음
// HelmetLoader가 Router 밖에 있어 useLocation을 사용할 수 없어서 pathname을 상태로 저장해 줌
const LocationWatcher = ({onPathName, onSearch}) => {
  const {pathname, search} = useLocation();

  useEffect(() => {
    const timeout = setTimeout(() => {
      window.dispatchEvent(new Event('scrollResetDone')); // 이벤트 추가 생성해서 reset 시점 관리 >> 방송국 댓글 하이라이트에서 사용중
      onPathName && onPathName(pathname);
      onSearch && onSearch(search);
    }, 500); //간헐적으로 메인에서 메인의 로고를 클릭해서 새로고침할시 씹히는 현상이 발생해서 광고가 노출이 안되는 현상이있어서 방어로직을 추가해봄
    return () => clearTimeout(timeout);
  }, [onPathName, onSearch, pathname, search]);
  return null;
};

const LowerStylePriority = () => {
  const location = useLocation();
  useEffect(() => {
    lowerStylePriority();
  }, [location.pathname]);
  return null;
};

const ChannelRoute = lazy(() => import('@/app/(main)/routes/channel'));
const HelpRoute = lazy(() => import('@/app/(main)/routes/help'));
const AwardRoute = lazy(() => import('@/app/(main)/routes/award'));

/**
 * 라우터 설정을 생성하는 함수
 * @returns {Object} createBrowserRouter 설정 객체
 */
const createRouterConfig = (onPathName, onSearch) => {
  return createBrowserRouter([
    {
      path: '/',
      errorElement: <ErrorFallback />,
      element: (
        <>
          <Layout />
          <LocationWatcher onPathName={onPathName} onSearch={onSearch} />
          <LowerStylePriority />
        </>
      ),
      children: [
        {
          index: true,
          element: <MyPlus />,
        },
        {
          path: ROUTE.SEARCH,
          element: <SearchRoute />,
        },
        {
          path: '/directory/*',
          element: <DirectoryRoute />,
        },
        {
          path: '/live/*',
          element: <LiveRoute />,
        },
        {
          path: '/my/*',
          element: (
            <PrivateRoute>
              <MyRoute />
            </PrivateRoute>
          ),
        },
        {
          path: '/station/*',
          element: <ChannelRoute />,
        },
        {
          path: ROUTE.STAR_RANK,
          element: <StarRankRoute />,
        },
        {
          path: ROUTE.HELP.ROOT,
          element: <HelpRoute />,
        },
      ],
    },
    {
      path: ROUTE.HELP.MY_INQUIRY_SURVEY,
      element: <SurveyLayout />,
      children: [
        {
          index: true,
          element: <HelpMyInquirySurvey />,
        },
      ],
    },
    {
      path: '/award/*',
      element: <AwardRoute />,
    },
  ]);
};

/**
 * 라우터 컴포넌트 래퍼
 * @param {Object} props - 컴포넌트 props
 * @param {Function} props.onPathName - 경로 변경 시 호출될 콜백
 * @param {Function} props.onSearch - 검색 파라미터 변경 시 호출될 콜백
 * @returns {JSX.Element} 라우터 컴포넌트
 */
const RouterWrapper = ({onPathName, onSearch, children}) => {
  const router = useMemo(() => createRouterConfig(onPathName, onSearch), []);

  return (
    <RouterProvider router={router} fallbackElement={<div>Loading...</div>}>
      {children}
    </RouterProvider>
  );
};

const RootNormalizer = () => {
  if (typeof window !== 'undefined') {
    const normalizedPath = normalizePath(window?.location.pathname);
    if (window?.location.pathname !== normalizedPath) {
      window?.location.replace(normalizedPath);
      return null;
    }
  }
};

const RootLayout = ({children}) => {
  const [render, setRender] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [search, setSearch] = useState('');
  const {setIsDarkMode} = useGlobalUIStore();
  const {setError} = useStationUIActions();

  const queryClient = useAppQueryClient(setError, [
    ...PHP_STATION_ERROR_CODE_VALUE,
    ...NODE_STATION_ERROR_CODE_VALUE,
  ]);

  const defaultQueryClient = useMemo(() => queryClient, []);

  useGlobalErrorHandler();

  const precheckDarkThemeBeforeSoopNavigation = () => {
    const isDark = getCookie('theme') === 'dark';
    setIsDarkMode(isDark);
    return isDark;
  };
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document
        .querySelector('html')
        .setAttribute(
          'dark',
          precheckDarkThemeBeforeSoopNavigation() ? 'true' : '',
        );

      //일정 버전 미만에서는 ResizeObserver를 지원하지않아서
      if (!window.ResizeObserver) {
        window.ResizeObserver = ResizeObserver;
      }
    }
    setRender(true);
    addIos15LessClassForSafari();
    setPosterImageObserver();
  }, []);

  useEffect(() => {
    if (!render) return;
    if (
      document.querySelector(
        'script[src*="securepubads.g.doubleclick.net/tag/js/gpt.js"]',
      )
    ) {
      return; // 이미 붙어있으면 무시
    }
    const script = document.createElement('script');
    script.src =
      'https://securepubads.g.doubleclick.net/tag/js/gpt.js?network-code=84015925';
    script.async = true;
    document.head.appendChild(script);
  }, [render]);

  return (
    <html lang='ko'>
      <RootNormalizer />
      <HelmetLoader routeName={routeName} search={search} />
      <body>
        <QueryClientProvider client={defaultQueryClient}>
          {render && (
            <>
              <RouterWrapper
                onPathName={setRouteName}
                onSearch={setSearch}></RouterWrapper>
            </>
          )}
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </body>
    </html>
  );
};

export default RootLayout;
